import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Folder,
  FolderPlus,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Trash2,
  Edit2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface FolderData {
  id: string;
  name: string;
  parent_id: string | null;
  visibility: string;
  created_at: string;
}

interface FolderManagerProps {
  folders: FolderData[];
  selectedFolderId: string | null;
  onFolderSelect: (folderId: string | null) => void;
  onCreateFolder: () => void;
  onFoldersChange: () => void;
}

export default function FolderManager({
  folders,
  selectedFolderId,
  onFolderSelect,
  onCreateFolder,
  onFoldersChange,
}: FolderManagerProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<FolderData | null>(null);
  const { toast } = useToast();

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;

    try {
      const { error } = await supabase
        .from('data_room_folders')
        .delete()
        .eq('id', folderToDelete.id);

      if (error) throw error;

      toast({
        title: 'Folder deleted',
        description: `"${folderToDelete.name}" has been deleted`,
      });

      if (selectedFolderId === folderToDelete.id) {
        onFolderSelect(null);
      }

      onFoldersChange();
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast({
        title: 'Failed to delete folder',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setFolderToDelete(null);
    }
  };

  const getChildFolders = (parentId: string | null) => {
    return folders.filter((f) => f.parent_id === parentId);
  };

  const renderFolder = (folder: FolderData, level: number = 0) => {
    const childFolders = getChildFolders(folder.id);
    const hasChildren = childFolders.length > 0;
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = selectedFolderId === folder.id;

    return (
      <div key={folder.id}>
        <div
          className={cn(
            'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent group',
            isSelected && 'bg-accent'
          )}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
        >
          {hasChildren ? (
            <button
              onClick={() => toggleFolder(folder.id)}
              className="p-0.5 hover:bg-accent rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}

          <div
            className="flex items-center gap-2 flex-1 min-w-0"
            onClick={() => onFolderSelect(folder.id)}
          >
            <Folder className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-sm truncate flex-1">{folder.name}</span>
            {folder.visibility !== 'INTERNAL' && (
              <Badge variant="outline" className="text-xs flex-shrink-0">
                {folder.visibility}
              </Badge>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setFolderToDelete(folder);
                  setDeleteDialogOpen(true);
                }}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {hasChildren && isExpanded && (
          <div>{childFolders.map((child) => renderFolder(child, level + 1))}</div>
        )}
      </div>
    );
  };

  const rootFolders = getChildFolders(null);

  return (
    <>
      <Card className="h-full">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Folders</h3>
          <Button size="sm" onClick={onCreateFolder}>
            <FolderPlus className="h-4 w-4 mr-2" />
            New
          </Button>
        </div>

        <ScrollArea className="h-[calc(100%-60px)]">
          <div className="p-2 space-y-1">
            <div
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent',
                selectedFolderId === null && 'bg-accent'
              )}
              onClick={() => onFolderSelect(null)}
            >
              <Folder className="h-4 w-4 text-primary" />
              <span className="text-sm">All Files</span>
            </div>

            {rootFolders.map((folder) => renderFolder(folder))}
          </div>
        </ScrollArea>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Folder</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{folderToDelete?.name}"? This will also delete all
              files and subfolders within it. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFolder}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
